import { AsyncPipe, DecimalPipe, CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { PageToolbarComponent, NotificationEmptyComponent } from '@next/core-lib/components';
import { TranslatePipe } from '@next/core-lib/i18n';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { MonthPickerComponent } from '../../../shared/components/month-picker/month-picker.component';
import { MilkDeliveryMonthSummaryItem } from '../../models/milk-delivery.model';
import * as MilkDeliveryActions from '../../store/actions/milk-delivery.actions';
import { getMilkDeliveryMonthSummary } from '../../store/selectors/milk-delivery.selectors';

@Component({
  selector: 'app-milk-month-delivery-summary',
  templateUrl: './milk-delivery-month-summary.component.html',
  styleUrls: ['./milk-delivery-month-summary.component.scss'],
  imports: [
    PageToolbarComponent,
    MonthPickerComponent,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    NotificationEmptyComponent,
    AsyncPipe,
    DecimalPipe,
    CurrencyPipe,
    TranslatePipe,
  ],
})
export class MilkDeliveryMonthSummaryComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject<Store<SiteState>>(Store);

  @ViewChild(MatSort) sort = new MatSort();
  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public dataSource = new MatTableDataSource<MilkDeliveryMonthSummaryItem>();
  public footer: MilkDeliveryMonthSummaryItem;
  public columns: {
    id: string;
    label: string;
    subLabel?: string;
    type: 'period' | 'number' | 'money';
    format?: string;
  }[] = [
    {
      id: 'period',
      label: 'dairyresults.milk-delivery-month-summary.period',
      type: 'period',
    },
    {
      id: 'kgMilk',
      label: 'dairyresults.milk-delivery-month-summary.milkKilograms',
      type: 'number',
      format: '1.0-0',
      subLabel: 'common.kilogram',
    },
    {
      id: 'fpcmKiloGrams',
      label: 'dairyresults.milk-delivery-month-summary.fpcmKiloGrams',
      type: 'number',
      format: '1.0-0',
      subLabel: 'common.kilogram',
    },
    {
      id: 'fatPercentage',
      label: 'dairyresults.milk-delivery-month-summary.fatPercentage',
      type: 'number',
      format: '1.2-2',
      subLabel: 'common.percentage',
    },
    {
      id: 'proteinPercentage',
      label: 'dairyresults.milk-delivery-month-summary.proteinPercentage',
      type: 'number',
      format: '1.2-2',
      subLabel: 'common.percentage',
    },
    {
      id: 'lactosePercentage',
      label: 'dairyresults.milk-delivery-month-summary.lactosePercentage',
      type: 'number',
      format: '1.2-2',
      subLabel: 'common.percentage',
    },
    {
      id: 'somaticCellCount',
      label: 'dairyresults.milk-delivery-month-summary.somaticCellcount',
      type: 'number',
      format: '1.0-0',
    },
    {
      id: 'ureum',
      label: 'dairyresults.milk-delivery-month-summary.ureum',
      type: 'number',
      format: '1.0-0',
    },
    {
      id: 'milkYield',
      label: 'dairyresults.milk-delivery-month-summary.milkYield',
      type: 'money',
    },
  ];
  private readonly destroy$ = new Subject<void>();
  private getMilkDeliveryMonthSummary$ = this.store.select(getMilkDeliveryMonthSummary);

  ngOnInit(): void {
    this.store.dispatch(
      SiteActions.updatePageTitle({ pageTitle: 'dairyresults.milk-delivery-month-summary.title' }),
    );
    this.dataSource.data = [];
    this.getMilkDeliveryMonthSummary$
      .pipe(
        filter((result) => !!result),
        takeUntil(this.destroy$),
      )
      .subscribe((result) => {
        this.dataSource.data = result.items;
        this.dataSource.sort = this.sort;
        this.footer = result.total;
      });

    const date = new Date();
    this.onDateChanged(
      new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1, 0, 0, 0)).toISOString(),
    );
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public columnIds = (): string[] => this.columns.map((item) => item.id);
  public hasData = (): boolean => this.dataSource?.data?.length > 0;

  public onDateChanged(date: string): void {
    const startDate = new Date(date);
    startDate.setMonth(new Date(date).getMonth() - 11);
    const endDate = new Date(date);
    endDate.setMonth(new Date(date).getMonth() + 1);

    this.store.dispatch(
      MilkDeliveryActions.loadMilkDeliveriesMonthSummaryRequest({
        start: startDate.toISOString(),
        end: endDate.toISOString(),
      }),
    );
  }

  public allignRight = (type: string): boolean => type === 'number' || type === 'money';
}
